import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Storylines from './Storylines';
import { PeopleAlt } from '@mui/icons-material';
import '../common.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCurrentKeysegment,
  toggleKeysegmentsAndStorylinesView,
} from '../redux/keysegmentsAndStorylinesSlice';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import PlaceholderThumbnail from './defaultThumbnail';
import EventIcon from '@mui/icons-material/Event';

const KeysegmentsAndStorylines = (props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { keySegments, currentKeySegment, storyLines, socketComplete, isCurationCancelled } = props;

  const showStorylinesComponent = useSelector(
    (state) => state.keysegmentsAndStorylines.showStorylinesComponent
  );

  const [keysegmentList, setKeysegmentList] = useState([]);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [index, setIndex] = useState(null);

  const checkIfSpeakerExists = speakers => {
    return speakers.some(speaker => speaker?.name !== 'UNKNOWN' && speaker?.type === 'guest');
  };
  const capitalize = str => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  const formattedDate = timestamp => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    });
  };

  const fetchSignedThumbnailUrls = useCallback(
    async (segment) => {
      if (index < keySegments?.length) {
        try {
          let newUrl = await generateSignedUrlForThumbnail(
            segment?.thumbnail_s3_bucket,
            segment?.thumbnail_s3_key
          );
          setThumbnailUrls((prevUrls) => ({
            ...prevUrls,
            [segment.clip_id]: newUrl,
          }));
        } catch (error) {
          console.error("Error fetching signed thumbnail URLs:", error);
        } finally {
          setIndex((prevIndex) => prevIndex + 1);
        }
      }
    },
    [index],
  );

  const generateSignedUrlForThumbnail = async (bucket, key) => {
    try {
      if (!bucket || !key) return null;

      const idToken = sessionStorage.getItem("idToken");
      if (!idToken) throw new Error("No ID Token found in sessionStorage");

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: fromCognitoIdentityPool({
          clientConfig: { region: process.env.REACT_APP_AWS_REGION },
          identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
          logins: {
            [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_USER_POOL_ID}`]:
              idToken,
          },
        }),
      });

      const command = new GetObjectCommand({ Bucket: bucket, Key: key });
      return await getSignedUrl(s3Client, command, { expiresIn: 3600 });
    } catch (error) {
      console.error("Error generating signed URL for thumbnail:", error);
      return null;
    }
  };

  const [sortValue, setSortValue] = useState('relevance');

  const handleSortChange = event => {
    setSortValue(event.target.value);
  };

  useEffect(() => {
    if (sortValue === 'recent') {
      setKeysegmentList(prevSegments => {
        const sortedSegments = [...prevSegments].sort((a, b) => {
          return b.video_timestamp?.epoch_millis - a.video_timestamp?.epoch_millis;
        });
        return sortedSegments;
      });
    } else {
      setKeysegmentList(keySegments);
    }
  }, [sortValue, keysegmentList]);

  useEffect(() => {
    if (keySegments?.length > 0) {
      if (index === null) setIndex(0);
      setKeysegmentList(keySegments);
      // fetchSignedThumbnailUrls(keySegments);
    } else {
      setKeysegmentList([]);
      setIndex(null);
    }
  }, [keySegments, index]);

  useEffect(() => {
    fetchSignedThumbnailUrls(keySegments[index]);
  }, [index]);

  return (
    <Box className="keysegments-storylines-wrapper" height="100%" flex={1}>
      <Box
        className="flex-center-center"
        gap="1rem"
        marginBottom={"1rem"}
        paddingInline={"5px"}
      >
        <Button
          onClick={() => {
            dispatch(toggleKeysegmentsAndStorylinesView(false));
          }}
          sx={{
            flex: 1,
            padding: "8px 24px",
            background: theme.palette.primary.dark,
            borderRadius: "0.5rem",
            border: !showStorylinesComponent
              ? `1px solid ${theme.palette.primary.light}`
              : "",
            textTransform: "none",
            color: !showStorylinesComponent
              ? theme.palette.primary.light
              : theme.palette.primary.white,
            fontWeight: 600,
            fontSize: "0.875rem",
            boxShadow: !showStorylinesComponent
              ? `0 0 3px 0 ${theme.palette.primary.light}`
              : "none",
            "&:hover": {
              boxShadow: `0 0 3px 0 ${theme.palette.primary.light}`,
            },
          }}
        >
          Key Segments
        </Button>
        <Button
          onClick={() => {
            dispatch(toggleKeysegmentsAndStorylinesView(true));
          }}
          sx={{
            flex: 1,
            padding: "8px 24px",
            background: theme.palette.primary.dark,
            borderRadius: "0.5rem",
            border: showStorylinesComponent
              ? `1px solid ${theme.palette.primary.light}`
              : "",
            textTransform: "none",
            color: showStorylinesComponent
              ? theme.palette.primary.light
              : theme.palette.primary.white,
            fontWeight: 600,
            fontSize: "0.875rem",
            boxShadow: showStorylinesComponent
              ? `0 0 3px 0 ${theme.palette.primary.light}`
              : "none",
            "&:hover": {
              boxShadow: `0 0 3px 0 ${theme.palette.primary.light}`,
            },
          }}
        >
          Video Stories
        </Button>
      </Box>

      {!showStorylinesComponent && (
        <>
          <Box
            width={"100%"}
            //   height={"72vh"}
            height={"90%"}
            overflow={"auto"}
            className={keysegmentList?.length < 1 && "flex-center-center"}
            sx={{
              background: theme.palette.primary.dark,
              borderRadius: "0.5rem",
              padding: "1rem",
            }}
          >
            {isCurationCancelled && !keysegmentList?.length > 0 && (
              <h5 style={{ textAlign: "center" }}>Query Canceled</h5>
            )}
            {!isCurationCancelled &&
              socketComplete &&
              keysegmentList?.length === 0 && (
                <Alert
                  className="flex-center-center"
                  variant="text"
                  severity="error"
                  sx={{ borderRadius: 48 }}
                >
                  No keysegments were found. Please try a different query.
                </Alert>
              )}
            {!isCurationCancelled &&
              !socketComplete &&
              keysegmentList?.length === 0 && (
                <CircularProgress
                  size={36}
                  thickness={6}
                  sx={{
                    color: "#AC1FFF",
                  }}
                />
              )}
            {keysegmentList?.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: '0.5rem' }}>
                  <div style={{ paddingRight: '0.25rem', fontSize: '0.875rem' }}>Sort by:</div>
                  <Select
                    value={sortValue}
                    onChange={handleSortChange}
                    displayEmpty
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.8rem',
                        paddingY: '0.5rem',
                      },
                    }}
                  >
                    <MenuItem value={'relevance'} sx={{ fontSize: '0.8rem' }}>
                      Relevance (default)
                    </MenuItem>
                    <MenuItem value={'recent'} sx={{ fontSize: '0.8rem' }}>
                      Most Recent
                    </MenuItem>
                  </Select>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, paddingBottom: '2rem' }}>
                  {keysegmentList.map(segment => {
                    return (
                      <li
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          dispatch(updateCurrentKeysegment(segment));
                        }}
                      >
                        <Box
                          mb={'1.5rem'}
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            gap: '0.5rem',
                            transition: 'scale 0.1s ease-in-out',
                            '&:hover': {
                              scale: '1.05',
                            },
                          }}
                        >
                          {imageErrors[segment.clip_id] || !thumbnailUrls[segment.clip_id] ? (
                            <Box className="flex-center-center" mb={'0.5rem'}>
                              <PlaceholderThumbnail />
                            </Box>
                          ) : (
                            <img
                              alt="thumbnail"
                              src={thumbnailUrls[segment.clip_id]}
                              style={{
                                width: '110px',
                                height: '65px',
                                borderRadius: '8px',
                              }}
                              onError={() => {
                                setImageErrors(prevErrors => ({
                                  ...prevErrors,
                                  [segment.clip_id]: true,
                                }));
                              }}
                            />
                          )}
                          <Box className="keysegments-text">
                            <Typography
                              sx={{
                                color: 'white',
                                fontWeight:
                                  segment?.clip_id === currentKeySegment?.clip_id
                                    ? 'bold'
                                    : 'normal',
                                fontSize: '0.75rem',
                              }}
                            >
                              {segment?.headline}
                            </Typography>
                            <Box>
                              <EventIcon
                                sx={{
                                  fontSize: '16px',
                                  mr: '6px',
                                  pt: '2px',
                                  color: '#666666',
                                }}
                              />
                              <Typography
                                color={'white'}
                                variant="caption"
                                style={{ fontSize: '0.7rem' }}
                              >
                                {formattedDate(segment?.video_timestamp?.epoch_millis)}
                              </Typography>
                            </Box>
                            <Box display={'flex'} alignItems={'start'}>
                              <Tooltip title="Speakers">
                                <PeopleAlt
                                  sx={{
                                    fontSize: '16px',
                                    mr: '6px',
                                    color: 'white',
                                    display: checkIfSpeakerExists(segment?.speakers)
                                      ? 'block'
                                      : 'none',
                                  }}
                                />
                              </Tooltip>
                            <Box>
                              {segment?.speakers?.map(speaker => {
                                if (speaker?.name !== 'UNKNOWN' && speaker?.type === 'guest') {
                                  return (
                                    <Tooltip
                                      title={
                                        speaker?.title
                                          ? `${capitalize(speaker?.name)}, ${capitalize(speaker?.title)}`
                                          : capitalize(speaker?.name)
                                      }
                                      key={speaker?.name}
                                    >
                                      <p className="truncate-w-48">
                                        {speaker?.title
                                          ? `${capitalize(speaker?.name)}, ${capitalize(speaker?.title)}`
                                          : capitalize(speaker?.name)}
                                      </p>
                                    </Tooltip>
                                  );
                                }
                              })}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Box>
        </>
      )}

      {showStorylinesComponent && (
        <Box
          width={"100%"}
          //   height={"72vh"}
          height={"90%"}
          overflow={"auto"}
          className={storyLines?.length < 1 && "flex-center-center"}
          sx={{
            background: theme.palette.primary.dark,
            borderRadius: "0.5rem",
            padding: "1rem",
          }}
        >
          <Storylines
            query={props.queryText}
            socketComplete={socketComplete}
            compilation={props.compilation}
            answerText={props.answerText}
            stories={storyLines}
            message={props.progressMessage}
            agentText={props.agentText}
            debug_messages={props.debugMessages}
            voiceOver={props?.voiceOver}
            isQA={props?.isQA}
            isCurationCancelled={isCurationCancelled}
          />
        </Box>
      )}
    </Box>
  );
};

export default KeysegmentsAndStorylines;
